import { useState, useEffect } from "react";
import { DataGrid, GridToolbar } from "@mui/x-data-grid";
import useApiServices from "../../../utils/useApiServices";
import {
  CREATE_SID,
  FETCH_PAYU_SID_LIST,
  FETCH_SURYODAY_SID_LIST,
} from "../../../utils/constant";
import CustomTip from "../../../common/CustomToolTip/CustomTip";
import AddCircleOutlineIcon from "@mui/icons-material/AddCircleOutline";
import CreateSIDForm from "./CreateSIDForm";
import CreateSuryodaySIDForm from "./CreateSuryodaySIDForm";
import CustomDialog from "../../common/CustomDialog";
import swal from "sweetalert";
import { MastersActionRegistry } from "../../../Privillages/ActionRegistry";
import useHasPrivilege from "../../../Privillages/useHasPrivilege";

const _initialFormData = {
  domainName: "",
  encKey: "",
  emailId: "",
  mobileNo: "",
  vehicleName: "",
  serviceProvider: "payu",
};

const SIDList = ({ type, toggleRightBar }) => {
  const { SECTION_KEY, EDIT_SID } = MastersActionRegistry;
  const hasEditSID = useHasPrivilege(SECTION_KEY, EDIT_SID);
  const { apiCallFnforGet, apiCallFnforPost } = useApiServices();
  const [activeProvider, setActiveProvider] = useState("payu");
  const [selectDialogOpen, setSelectDialogOpen] = useState(false);
  const [payuDialogOpen, setPayuDialogOpen] = useState(false);
  const [suryodayDialogOpen, setSuryodayDialogOpen] = useState(false);

  // Form-related states
  const [formData, setFormData] = useState({ ..._initialFormData });
  const [formErrors, setFormErrors] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [payuList, setPayuList] = useState([]);
  const [suryodayList, setSuryodayList] = useState([]);

  const fetchData = async () => {
    try {
      if (activeProvider === "payu") {
        const response = await apiCallFnforGet(FETCH_PAYU_SID_LIST);
        if (response?.statusCode === 200) {
          setPayuList(response?.data);
        } else {
          setPayuList([]);
        }
      } else if (activeProvider === "suryoday") {
        const response = await apiCallFnforGet(FETCH_SURYODAY_SID_LIST);
        if (response?.statusCode === 200) {
          setSuryodayList(response?.data);
        } else {
          setSuryodayList([]);
        }
      }
    } catch (error) {
      console.log("Error fetching SID list:", error);
    }
  };

  useEffect(() => {
    fetchData();
  }, [activeProvider]);

  const handleCreateSIDClick = () => {
    if (activeProvider === "payu") setPayuDialogOpen(true);
    else if (activeProvider === "suryoday") setSuryodayDialogOpen(true);
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  const handleCloseModal = () => {
    setPayuDialogOpen(false);
    setFormErrors({});
    setFormData({ ..._initialFormData });
  };

  const validateForm = (data) => {
    try {
      const errors = {};

      if (!data?.domainName?.trim())
        errors.domainName = "Domain name is required";
      if (!data?.encKey?.trim()) errors.encKey = "Encryption key is required";

      if (!data?.emailId?.trim()) {
        errors.emailId = "Email ID is required";
      } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(data?.emailId)) {
        errors.emailId = "Invalid email format";
      }

      if (!data?.mobileNo?.trim()) {
        errors.mobileNo = "Mobile number is required";
      } else if (!/^[6-9]\d{9}$/.test(data?.mobileNo)) {
        errors.mobileNo = "Enter a valid 10-digit mobile number";
      }

      if (!data?.vehicleName?.trim())
        errors.vehicleName = "Vehicle name is required";

      if (!data?.serviceProvider?.trim())
        errors.serviceProvider = "Service provider is required";

      setFormErrors(errors);
      return Object.keys(errors).length === 0;
    } catch (error) {
      console.error("Error validating form:", error);
      return false;
    }
  };

  const handleCreateSID = async (e) => {
    e?.preventDefault();

    if (!validateForm(formData)) return;

    setIsSubmitting(true);
    try {
      const res = await apiCallFnforPost(CREATE_SID, formData);

      if (res?.statusCode === 200) {
        swal({
          title: "Success",
          text: res?.message || "SID created successfully!",
          icon: "success",
        });
        setFormData({ ..._initialFormData });
        setFormErrors({});
        setPayuDialogOpen(false);
        fetchData();
      } else {
        swal({
          title: "Error",
          text: res?.message || "Failed to create SID.",
          icon: "error",
        });
      }
    } catch (error) {
      console.error("SID creation error:", error);
      swal({
        title: "Error",
        text: "Something went wrong. Please try again later.",
        icon: "error",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSelectOption = (option) => {
    setSelectDialogOpen(false);
    if (option === "payu") setPayuDialogOpen(true);
    else if (option === "suryoday") setSuryodayDialogOpen(true);
  };

  const payuColumns = [
    { headerName: "Domain Name", field: "domainName", minWidth: 200 },
    { headerName: "Processor", field: "processor", minWidth: 150 },
    { headerName: "MID", field: "mid", minWidth: 200 },
    { headerName: "Vehicle Name", field: "vehicleName", minWidth: 250 },
    { headerName: "Email", field: "emailId", minWidth: 200 },
    { headerName: "Mobile", field: "mobileNo", minWidth: 150 },
  ];

  const suryodayColumns = [
    { headerName: "Domain Name", field: "domainName", minWidth: 200 },
    { headerName: "Processor", field: "processor", minWidth: 150 },
    { headerName: "MID", field: "mid", minWidth: 200 },
    { headerName: "Vehicle Name", field: "vehicleName", minWidth: 250 },
    { headerName: "Username", field: "username", minWidth: 150 },
    { headerName: "Password", field: "password", minWidth: 250 },
  ];

  const payuData = payuList.map((item, i) => ({
    ...item,
    id: i,
  }));

  const suryodayData = suryodayList.map((item, i) => ({
    ...item,
    id: i,
  }));

  return (
    <>
      <div className="container-fluid">
        <div className="d-flex justify-content-between align-items-center">
          <div className="d-flex align-items-center">
            <button
              type="button"
              class="btn-close m-3"
              onClick={toggleRightBar}
            ></button>

            <div className="fs-2 text-dark fw-bold">SID List</div>
          </div>

          <div className="d-flex align-items-center gap-3 me-4">
            <select
              className="form-select form-select-sm"
              style={{ width: "150px" }}
              value={activeProvider}
              onChange={(e) => setActiveProvider(e.target.value)}
            >
              <option value="payu">PayU</option>
              <option value="suryoday">Suryoday</option>
            </select>

            {/* Create SID Icon */}
            {hasEditSID && (
              <CustomTip size="18" placement="top" title="Create SID">
                <div onClick={handleCreateSIDClick}>
                  <AddCircleOutlineIcon fontSize="large" />
                </div>
              </CustomTip>
            )}
          </div>
        </div>
        <div className="row">
          <div className="col-12">
            <div className="card">
              <div className="col-12">
                <div className="card-body">
                  <div className="table-responsive">
                    <DataGrid
                      rows={activeProvider === "payu" ? payuData : suryodayData}
                      columns={
                        activeProvider === "payu"
                          ? payuColumns
                          : suryodayColumns
                      }
                      slots={{ toolbar: GridToolbar }}
                      slotProps={{ toolbar: { showQuickFilter: true } }}
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* SELECT SID CREATION TYPE DIALOG -- not using for now  */}
      <CustomDialog
        open={selectDialogOpen}
        onClose={() => setSelectDialogOpen(false)}
        title="Select SID Type"
      >
        <div className="container py-2">
          <div className="row g-2 justify-content-center">
            {[
              { label: "PayU", value: "payu", color: "primary" },
              {
                label: "Suryoday",
                value: "suryoday",
                color: "success",
              },
              // Add more easily here...
            ].map((option) => (
              <div key={option.value} className="col-6 col-md-4">
                <button
                  className={`btn btn-sm w-100 btn-${option.color}`}
                  onClick={() => handleSelectOption(option.value)}
                >
                  {option.label}
                </button>
              </div>
            ))}
          </div>
        </div>
      </CustomDialog>

      {/*  PayU SID Form */}
      <CreateSIDForm
        open={payuDialogOpen}
        onHandleClose={handleCloseModal}
        formData={formData}
        handleChange={handleChange}
        handleSubmit={handleCreateSID}
        isSubmitting={isSubmitting}
        formErrors={formErrors}
      />

      {/*  Suryoday SID Form */}
      <CreateSuryodaySIDForm
        open={suryodayDialogOpen}
        onHandleClose={() => setSuryodayDialogOpen(false)}
        formData={formData || {}}
        handleChange={handleChange}
        isSubmitting={isSubmitting}
        formErrors={formErrors || {}}
        refetchData={fetchData}
      />
    </>
  );
};

export default SIDList;
